// Writing CSV, for the downloadable import error report (plan §4.2, step 4).
//
// The report is opened in a spreadsheet by the person fixing their file, so a cell must
// never be read as a formula ("CSV injection"): a value starting with =, +, -, @, a tab or
// a carriage return gets a leading apostrophe, which spreadsheets show as text. This also
// affects a negative number such as "-5"; in an error report that is the safe trade.

import type { CsvDelimiter } from './parse.ts';

/** Line break between records, as RFC 4180 specifies. */
export const CSV_LINE_BREAK = '\r\n';

const FORMULA_START = /^[=+\-@\t\r]/;

/** Makes a value safe to open in a spreadsheet, as described above. */
export function neutraliseFormula(value: string): string {
  return FORMULA_START.test(value) ? `'${value}` : value;
}

function quoteIfNeeded(value: string, delimiter: CsvDelimiter): string {
  const needsQuotes =
    value.includes(delimiter) ||
    value.includes('"') ||
    value.includes('\n') ||
    value.includes('\r') ||
    value !== value.trim();
  return needsQuotes ? `"${value.replaceAll('"', '""')}"` : value;
}

/** One CSV record, without the line break. Values are quoted only when they need it. */
export function toCsvRecord(values: readonly string[], delimiter: CsvDelimiter = ','): string {
  return values.map((raw) => quoteIfNeeded(neutraliseFormula(raw), delimiter)).join(delimiter);
}

/**
 * A cell in a report export: text, which is made formula-safe, or a plain decimal number
 * such as `-1500.00`, written as it is so a spreadsheet can add it up. A "number" that
 * isn't only digits, one optional leading minus and one optional decimal point is refused:
 * that is the only way a leading minus reaches the file unneutralised.
 */
export type CsvCell = string | { readonly number: string };

const PLAIN_DECIMAL = /^-?\d+(?:\.\d+)?$/;

export function toCsvRecordCells(cells: readonly CsvCell[], delimiter: CsvDelimiter = ','): string {
  return cells
    .map((cell) => {
      if (typeof cell === 'string') return quoteIfNeeded(neutraliseFormula(cell), delimiter);
      if (!PLAIN_DECIMAL.test(cell.number))
        throw new RangeError('A numeric CSV cell must be a plain decimal number');
      return cell.number;
    })
    .join(delimiter);
}
